import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Search, FileText, CheckCircle2, XCircle, Calendar, User, ExternalLink, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FilingsSearch = () => {
  const [gakNumber, setGakNumber] = useState("");
  const [gakYear, setGakYear] = useState(String(new Date().getFullYear()));
  const [eakNumber, setEakNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const fmtDmy = (iso) => {
    if (!iso) return "—";
    try {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    } catch (_) { return iso; }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!gakNumber && !eakNumber) {
      toast.error("Συμπληρώστε τουλάχιστον ΓΑΚ ή ΕΑΚ");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const params = new URLSearchParams();
      if (gakNumber) params.append("gak_number", gakNumber);
      if (gakYear && gakNumber) params.append("gak_year", gakYear);
      if (eakNumber) params.append("eak_number", eakNumber);
      const res = await axios.get(`${API}/case-filings/search?${params}`);
      setResults(res.data);
      if (!res.data?.results?.length) toast.info("Δεν βρέθηκαν αποτελέσματα");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Σφάλμα αναζήτησης");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="filings-search-page">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-purple-100 rounded-lg">
          <Search className="w-6 h-6 text-purple-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Αναζήτηση Καταθέσεων Αντιδίκου</h1>
          <p className="text-sm text-slate-500">Έλεγξε αν έχουν κατατεθεί Προτάσεις ή Προσθήκη-Αντικρούση σε υπόθεση</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Αναζήτηση με ΓΑΚ ή ΕΑΚ</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>ΓΑΚ Αριθμός</Label>
              <Input
                type="number"
                value={gakNumber}
                onChange={(e) => setGakNumber(e.target.value)}
                placeholder="π.χ. 12345"
                data-testid="filings-gak-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ΓΑΚ Έτος</Label>
              <Input
                type="number"
                value={gakYear}
                onChange={(e) => setGakYear(e.target.value)}
                placeholder="π.χ. 2025"
                data-testid="filings-gak-year-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ΕΑΚ (προαιρετικό)</Label>
              <Input
                value={eakNumber}
                onChange={(e) => setEakNumber(e.target.value)}
                placeholder="π.χ. 1234/5"
                data-testid="filings-eak-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="filings-search-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Αναζήτηση
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {results?.results?.map((c) => (
        <Card key={c.case_id} className="border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-white">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  ΓΑΚ {c.gak_number}/{c.gak_year}
                  {c.eak_number && <Badge variant="outline">ΕΑΚ {c.eak_number}</Badge>}
                  <Badge>{c.court_type}</Badge>
                </CardTitle>
                <p className="text-sm text-slate-600 mt-1">{c.subject || "—"}</p>
              </div>
              <Link to={`/cases/${c.case_id}`}>
                <Button variant="outline" size="sm" data-testid={`filings-open-case-${c.case_id}`}>
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Άνοιγμα Υπόθεσης
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Στοιχεία υπόθεσης */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-slate-500 text-xs">Κατάθεση</span>
                <div className="font-medium">{fmtDmy(c.filing_date)}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">Δικάσιμος</span>
                <div className="font-medium">{c.last_hearing_date || fmtDmy(c.hearing_date)}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">Δικαστής</span>
                <div className="font-medium">{c.assigned_judge || <span className="text-slate-400">—</span>}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">Βαθμός</span>
                <div className="font-medium">{c.assigned_judge_grade || <span className="text-slate-400">—</span>}</div>
              </div>
            </div>

            {/* Προθεσμίες */}
            <div className="bg-slate-50 p-3 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                <span><b>Προθεσμία Προτάσεων:</b> {fmtDmy(c.proposals_deadline)}</span>
                <Badge variant={c.proposals_filed_count > 0 ? "default" : "outline"} className={c.proposals_filed_count > 0 ? "bg-emerald-100 text-emerald-700" : ""}>
                  {c.proposals_filed_count > 0 ? `✓ ${c.proposals_filed_count} κατατεθείσες` : "καμία καταχώρηση"}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                <span><b>Προθ. Προσθήκης:</b> {fmtDmy(c.addition_deadline)}</span>
                <Badge variant={c.addition_filed_count > 0 ? "default" : "outline"} className={c.addition_filed_count > 0 ? "bg-emerald-100 text-emerald-700" : ""}>
                  {c.addition_filed_count > 0 ? `✓ ${c.addition_filed_count} κατατεθείσες` : "καμία καταχώρηση"}
                </Badge>
              </div>
            </div>

            {/* Καταθέσεις ανά διάδικο */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" /> Καταθέσεις ανά Διάδικο
              </h4>
              {c.filings_by_party && c.filings_by_party.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">Διάδικος</th>
                        <th className="text-left p-2">Ρόλος</th>
                        <th className="text-left p-2">Προτάσεις</th>
                        <th className="text-left p-2">Προσθήκη-Αντικρ.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.filings_by_party.map((p, idx) => (
                        <tr key={idx} className="border-t" data-testid={`filings-party-row-${idx}`}>
                          <td className="p-2 font-medium">{p.party_name}</td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">
                              {p.party_type === "plaintiff" ? "Ενάγων" : (p.party_type === "defendant" ? "Εναγόμενος" : p.party_type)}
                            </Badge>
                          </td>
                          <td className="p-2">
                            {p.proposals ? (
                              <div className="flex items-center gap-2 text-emerald-700">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-xs">
                                  {fmtDmy(p.proposals.filing_date)}
                                  {p.proposals.filing_time && ` ${p.proposals.filing_time}`}
                                  {p.proposals.folders_count > 0 && ` · ${p.proposals.folders_count} φακ.`}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-slate-400 text-xs">
                                <XCircle className="w-3.5 h-3.5" /> δεν έχει κατατεθεί
                              </div>
                            )}
                          </td>
                          <td className="p-2">
                            {p.addition_counter ? (
                              <div className="flex items-center gap-2 text-emerald-700">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-xs">
                                  {fmtDmy(p.addition_counter.filing_date)}
                                  {p.addition_counter.filing_time && ` ${p.addition_counter.filing_time}`}
                                  {p.addition_counter.folders_count > 0 && ` · ${p.addition_counter.folders_count} φακ.`}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-slate-400 text-xs">
                                <XCircle className="w-3.5 h-3.5" /> δεν έχει κατατεθεί
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400 italic border border-dashed rounded-lg">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  Δεν υπάρχουν καταχωρημένες καταθέσεις για κανέναν διάδικο.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {results && results.results?.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            Δεν βρέθηκε υπόθεση με τα στοιχεία αυτά.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FilingsSearch;
